import type { ReactNode } from "react";
import { useState } from "react";
import { SearchLg } from "@untitledui/icons";
import { Input } from "@/components/base/input/input";
import { cx } from "@/utils/cx";
import { MobileNavigationHeader } from "../base-components/mobile-header";
import { NavAccountCard } from "../base-components/nav-account-card";
import { NavItemBase } from "../base-components/nav-item";
import { NavList } from "../base-components/nav-list";
import type { NavItemType } from "../config";
import { useCommandMenu } from "@/components/application/command-menu/use-command-menu";
import { SignOutConfirmModal } from "../sign-out-confirm-modal";
import { useAccount } from "@/providers/account-provider";
import { isRouteActive } from "@/utils/route-matcher";
import { EveLogo } from "@/components/foundations/logo/eve-logo";
import { VersionFooter } from "@/components/base/version-footer/version-footer";

interface SidebarNavigationProps {
    /** URL of the currently active item. */
    activeUrl?: string;
    /** List of items to display. */
    items: NavItemType[];
    /** List of footer items to display. */
    footerItems?: NavItemType[];
    /** Feature card to display. */
    featureCard?: ReactNode;
    /** Whether to show the account card. */
    showAccountCard?: boolean;
    /** Whether to hide the right side border. */
    hideBorder?: boolean;
    /** Additional CSS classes to apply to the sidebar. */
    className?: string;
}

export const SidebarNavigationSimple = ({
    activeUrl,
    items,
    footerItems = [],
    featureCard,
    showAccountCard = true,
    hideBorder = false,
    className,
}: SidebarNavigationProps) => {
    const MAIN_SIDEBAR_WIDTH = 296;
    const { open, shouldPreventAutoOpen } = useCommandMenu();
    const { signOut } = useAccount();
    const [showSignOutModal, setShowSignOutModal] = useState(false);
    const env = import.meta.env.VITE_ENV;
    const hasEnvironmentBanner = env !== "production";

    // Verifica se un item è il logout
    const isLogoutItem = (item: NavItemType) => {
        const label = item.label?.toLowerCase() || '';
        const href = item.href?.toLowerCase() || '';
        return label.includes('logout') || label.includes('log out') ||
            href.includes('/auth/login') || href.includes('/logout');
    };

    const handleFooterItemClick = (item: NavItemType, e: React.MouseEvent) => {
        if (isLogoutItem(item)) {
            e.preventDefault();
            e.stopPropagation();
            setShowSignOutModal(true);
        }
    };

    const handleSearchClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Apri sempre quando si clicca (forza l'apertura anche se preventata)
        open(true);
    };

    const handleSearchFocus = () => {
        // Non aprire se l'apertura automatica è stata preventata (ad esempio dopo ESC)
        if (shouldPreventAutoOpen) {
            return;
        }
        // Apri il menu quando l'input riceve focus
        open();
    };

    const content = (
        <aside
            style={
                {
                    "--width": `${MAIN_SIDEBAR_WIDTH}px`,
                } as React.CSSProperties
            }
            className={cx(
                "flex h-full w-full max-w-full flex-col justify-between overflow-auto bg-primary pt-4 lg:w-(--width) lg:pt-6",
                !hideBorder && "border-secondary md:border-r",
                className,
            )}
        >
            <div className="flex flex-col gap-5 px-4 lg:px-5">
                <div className="flex items-center gap-2">
                    <EveLogo className="h-6 w-6" />
                    <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">Eve Mind</h2>
                </div>
                <div
                    onClick={handleSearchClick}
                    className="cursor-pointer"
                >
                    <Input
                        shortcut
                        size="sm"
                        aria-label="Search"
                        placeholder="Search"
                        icon={SearchLg}
                        isReadOnly
                        onFocus={handleSearchFocus}
                    />
                </div>
            </div>

            <NavList activeUrl={activeUrl} items={items} />

            <div className="mt-auto flex flex-col gap-4 px-2 py-4 lg:px-4 lg:py-6">

                {featureCard}

                {footerItems.length > 0 && (
                    <ul className="flex flex-col">
                        {footerItems.map((item) => (
                            <li key={item.label} className="py-0.5">
                                <NavItemBase
                                    badge={item.badge}
                                    icon={item.icon}
                                    href={item.href}
                                    type="link"
                                    current={isRouteActive(item.href, activeUrl)}
                                    onClick={(e) => handleFooterItemClick(item, e)}
                                >
                                    {item.label}
                                </NavItemBase>
                            </li>
                        ))}
                    </ul>
                )}

                {showAccountCard && <NavAccountCard />}

                {showAccountCard && <VersionFooter />}
            </div>
        </aside>
    );

    return (
        <>
            {/* Mobile header navigation */}
            <MobileNavigationHeader>{content}</MobileNavigationHeader>

            {/* Desktop sidebar navigation */}
            <div
                className={cx(
                    "hidden lg:fixed lg:left-0 lg:flex",
                    hasEnvironmentBanner ? "lg:top-10 lg:bottom-0" : "lg:inset-y-0"
                )}
            >
                {content}
            </div>

            {/* Placeholder to take up physical space because the real sidebar has `fixed` position. */}
            <div
                style={{
                    paddingLeft: MAIN_SIDEBAR_WIDTH,
                }}
                className="invisible hidden lg:sticky lg:top-0 lg:bottom-0 lg:left-0 lg:block"
            />

            {/* Sign Out Confirmation Modal */}
            <SignOutConfirmModal
                isOpen={showSignOutModal}
                onClose={() => setShowSignOutModal(false)}
                onConfirm={() => {
                    signOut();
                    setShowSignOutModal(false);
                }}
            />
        </>
    );
};
